export type PostalLabelFormat = "US" | "CA";
export type PostalValidationFormat = "US" | "CA" | "either" | "other";

const US_ZIP = /^\d{5}(?:-\d{4})?$/;
const CA_POSTAL = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const OTHER_POSTAL = /^[A-Za-z0-9][A-Za-z0-9 \-]{2,11}$/;

export function isCardPostalCodeComplete(
  value: string,
  format: PostalValidationFormat,
): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  const isUs = US_ZIP.test(trimmed);
  const isCa = CA_POSTAL.test(trimmed);

  if (format === "US") return isUs;
  if (format === "CA") return isCa;
  if (format === "other") return OTHER_POSTAL.test(trimmed);
  return isUs || isCa;
}

/**
 * Whether the postal field should stop the card form from being "complete".
 *
 * - Required: must be a complete ZIP/postal for the resolved format.
 * - Optional: an empty field is fine, but anything typed must still be a
 *   complete ZIP/postal so a typo doesn't get sent to Stripe as-is.
 */
export function isCardPostalCodeAcceptable(
  value: string,
  format: PostalValidationFormat,
  required: boolean,
): boolean {
  if (!required && value.trim() === "") return true;
  return isCardPostalCodeComplete(value, format);
}

/**
 * Split Card Elements do not expose BIN country. Once Stripe has a complete
 * number/expiry/CVC we can read `paymentMethod.card.country` and match the
 * old Card Element: US cards → ZIP, CA cards → postal.
 */
export function resolvePostalFormats(
  cardCountry: string | null | undefined,
  typedValue: string,
  fallbackRegion: "CA" | "US",
): { label: PostalLabelFormat; validation: PostalValidationFormat } {
  if (cardCountry === "US") {
    return { label: "US", validation: "US" };
  }
  if (cardCountry === "CA") {
    return { label: "CA", validation: "CA" };
  }
  if (cardCountry) {
    return { label: "CA", validation: "other" };
  }

  const trimmed = typedValue.trim();
  if (/^\d/.test(trimmed)) {
    return { label: "US", validation: "either" };
  }
  if (/^[A-Za-z]/.test(trimmed)) {
    return { label: "CA", validation: "either" };
  }
  return { label: fallbackRegion, validation: "either" };
}
